import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
} from "@mui/material";
import kebabCase from "lodash/kebabCase";

import { ConfigTable } from "../../types";
import { RenderCellTable } from "../RenderCellTable/render-cell-table";

import type { ReactElement } from "react";

type Props<T> = {
  configTable: ConfigTable<T>[];
  data: T[];
  onRowClick?: (row: T) => void;
  stripRows?: boolean;
  getRowKey?: (row: T, index: number) => string | number;
};

const defaultGetRowKey = <T,>(row: T, index: number): string | number => {
  if (row && typeof row === "object" && "id" in row) {
    const { id } = row as { id: unknown };
    if (typeof id === "string" || typeof id === "number") {
      return id;
    }
  }
  return index;
};

const DataTable = <T,>({
  configTable,
  data,
  onRowClick,
  stripRows = false,
  getRowKey = defaultGetRowKey,
}: Props<T>): ReactElement => (
  <TableContainer data-testid="ds-data-table">
    <Table aria-label="data-table">
      <TableHead>
        <TableRow>
          {configTable.map((column) => (
            <TableCell
              data-testid={`ds-data-table-header-${kebabCase(column.field)}`}
              key={column.field}
              align={column.align}
              style={{ width: column.width }}
            >
              {column.headerName}
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {data.map((row, i) => (
          <TableRow
            data-testid={`ds-data-table-row-${i}`}
            key={getRowKey(row, i)}
            hover={!!onRowClick}
            onClick={(e) => {
              e.preventDefault();
              onRowClick?.(row);
            }}
            tabIndex={onRowClick ? 0 : undefined}
            onKeyDown={
              onRowClick
                ? (event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onRowClick(row);
                    }
                  }
                : undefined
            }
            role={onRowClick ? "link" : undefined}
            aria-label={
              onRowClick ? `View details for row ${i + 1}` : undefined
            }
            sx={{
              ...(onRowClick && {
                cursor: "pointer",
              }),
              ...(stripRows && {
                backgroundColor: i % 2 !== 0 ? "neutrals.neutral_50" : "white",
              }),
            }}
          >
            {configTable.map((column, j) => (
              <TableCell
                data-testid={`ds-data-table-cell-${i}-${j}`}
                component={j === 0 ? "th" : "td"}
                key={column.field}
                align={column.align}
                style={{ width: column.width }}
                sx={column.rowCellSx}
              >
                <RenderCellTable column={column} row={row} />
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

export { DataTable };
